import React, { useEffect, useRef, useState } from 'react';
import { TOP_CRYPTO_PAIRS, CFD_INSTRUMENTS } from '../constants/tradingPairs';
import { getListedCfdInstrument } from '../../supabase/functions/_shared/cfdInstruments';

interface TradingChartProps {
  selectedPair: string;
}

const NASDAQ_STOCKS = new Set([
  'AAPL', 'MSFT', 'GOOGL', 'AMZN', 'TSLA', 'META', 'NFLX', 'NVDA',
  'AMD', 'INTC', 'ORCL', 'ADBE', 'PYPL', 'MRNA', 'PEP', 'CSCO',
  'COST', 'AVGO', 'TXN', 'QCOM', 'AMAT', 'INTU', 'SBUX', 'AMGN',
  'SHOP', 'MCD', 'ISRG', 'REGN', 'BKNG', 'ADP', 'GILD', 'MDLZ',
  'ADI', 'LRCX', 'KLAC', 'SNPS', 'CDNS', 'MRVL', 'FTNT', 'PANW',
  'ABNB', 'CRWD', 'DDOG', 'ZS', 'TEAM', 'WDAY', 'MELI', 'MNST',
  'KDP', 'EXC', 'XEL', 'EA', 'TTWO', 'ATVI', 'NXPI', 'MCHP',
  'ON', 'SWKS', 'CPRT', 'FAST', 'ODFL', 'CSGP', 'ANSS', 'DXCM',
  'ILMN', 'BIIB', 'SIRI', 'LULU', 'ROST', 'PCAR', 'IDXX', 'WBD',
  'LCID', 'RIVN', 'HOOD', 'COIN', 'ROKU', 'SNAP', 'PINS', 'ZM',
  'DOCU', 'OKTA', 'NET', 'TTD', 'U', 'RBLX', 'DASH', 'LYFT',
  'PLTR'
]);

const NYSE_STOCKS = new Set([
  'BA', 'JPM', 'GS', 'BAC', 'WMT', 'DIS', 'CVX', 'IBM', 'UBER',
  'PFE', 'KO', 'XOM', 'T', 'V', 'MA', 'CRM', 'LOW', 'HD', 'CAT',
  'HON', 'LLY', 'UNH', 'ABT', 'CVS', 'WFC', 'MS', 'AXP', 'BLK',
  'C', 'BK', 'TGT', 'NKE', 'SNOW', 'BRK.B', 'JNJ', 'PG', 'MMM',
  'GE', 'F', 'GM', 'DE', 'RTX', 'LMT', 'NOC', 'GD', 'SQ', 'SPOT',
  'TWLO', 'NOW', 'HUBS'
]);

function getStockExchange(symbol: string): string {
  if (NASDAQ_STOCKS.has(symbol)) return `NASDAQ:${symbol}`;
  if (NYSE_STOCKS.has(symbol)) return `NYSE:${symbol}`;
  return `NASDAQ:${symbol}`;
}

function getEtfExchange(symbol: string): string {
  if (symbol === 'CAC') return 'EURONEXT:PX1';
  if (symbol === 'ASX') return 'ASX:XJO';
  return `AMEX:${symbol}`;
}

function normalizeStockSymbol(symbol: string): string {
  return symbol.replace(/[^A-Z.]/g, '');
}

function formatSymbolForTradingView(symbol: string): string {
  const listing = getListedCfdInstrument(symbol);
  if (listing) return listing.tradingViewSymbol;
  const cryptoPair = TOP_CRYPTO_PAIRS.find(pair => pair.symbol === symbol);
  if (cryptoPair) return `BYBIT:${symbol}.P`;
  if (symbol.endsWith('USDT')) return `BYBIT:${symbol}.P`;

  const cfdInstrument = CFD_INSTRUMENTS.find(instrument => instrument.symbol === symbol);
  if (cfdInstrument) {
    if (cfdInstrument.type === 'forex') return symbol.replace('/', '');
    if (cfdInstrument.type === 'stock') return getStockExchange(normalizeStockSymbol(symbol));
    if (cfdInstrument.type === 'index') return getEtfExchange(symbol);
    if (cfdInstrument.type === 'commodity') {
      switch (symbol) {
        case 'XAUUSD': case 'XAU/USD': return 'XAUUSD';
        case 'XAGUSD': case 'XAG/USD': return 'XAGUSD';
        case 'XPTUSD': case 'XPT/USD': return 'XPTUSD';
        case 'XPDUSD': case 'XPD/USD': return 'XPDUSD';
        case 'WTICOUSD': case 'WTI/USD': case 'WTICO/USD': return 'OANDA:WTICOUSD';
        case 'BCOUSD': case 'Brent Crude': case 'Brent Crude Oil': case 'BCO/USD': return 'OANDA:BCOUSD';
        case 'NATGASUSD': case 'NATGAS/USD': case 'Natural Gas': return 'OANDA:NATGASUSD';
        case 'CORNUSD': return 'ZW1!';
        case 'SOYBNUSD': case 'Soybean': return 'CBOT:ZS1!';
        default: return symbol;
      }
    }
  }

  const normalized = normalizeStockSymbol(symbol);
  if (NASDAQ_STOCKS.has(normalized) || NYSE_STOCKS.has(normalized)) {
    return getStockExchange(normalized);
  }

  return symbol;
}

const TradingChart: React.FC<TradingChartProps> = ({ selectedPair }) => {
  const listing = getListedCfdInstrument(selectedPair);
  const hasCurrencyConversion = listing && listing.quoteCurrency !== 'USD';
  const containerRef = useRef<HTMLDivElement>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!selectedPair) {
      setError('No trading pair selected');
      return;
    }

    setError(null);

    if (!containerRef.current) {
      setError('Chart container not available');
      return;
    }

    containerRef.current.innerHTML = '';

    const tradingViewSymbol = formatSymbolForTradingView(selectedPair);

    const widgetContainer = document.createElement('div');
    widgetContainer.className = 'tradingview-widget-container';
    widgetContainer.style.height = '100%';
    widgetContainer.style.width = '100%';

    const widgetInner = document.createElement('div');
    widgetInner.className = 'tradingview-widget-container__widget';
    widgetInner.style.height = 'calc(100% - 32px)';
    widgetInner.style.width = '100%';
    widgetContainer.appendChild(widgetInner);

    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.src = 'https://s3.tradingview.com/external-embedding/embed-widget-advanced-chart.js';
    script.async = true;
    script.innerHTML = JSON.stringify({
      autosize: true,
      symbol: tradingViewSymbol,
      ticker: tradingViewSymbol,
      interval: "1",
      timezone: "Etc/UTC",
      theme: "light",
      style: "1",
      locale: "en",
      allow_symbol_change: false,
      support_host: "https://www.tradingview.com",
      backgroundColor: "#ffffff",
      gridColor: "#e2e8f0",
      hide_top_toolbar: false,
      hide_legend: false,
      save_image: false,
      studies: ["Volume@tv-basicstudies"]
    });

    script.onerror = () => {
      setError('Failed to load TradingView library');
    };

    widgetContainer.appendChild(script);
    containerRef.current.appendChild(widgetContainer);

    return () => {
      if (containerRef.current) {
        containerRef.current.innerHTML = '';
      }
    };
  }, [selectedPair]);

  return (
    <div className="bg-white h-full w-full relative overflow-hidden flex flex-col">
      {hasCurrencyConversion && (
        <p className="px-3 py-1 text-xs text-slate-600 border-b border-slate-200">
          Underlying chart: {listing.quoteCurrency}. CFD order prices, margin and P&amp;L: USD.
        </p>
      )}
      {error && (
        <div className="absolute inset-0 flex items-center justify-center bg-white/95 z-10">
          <div className="text-slate-600 text-center">
            <p className="mb-2">{error}</p>
            <p className="text-sm">Please select a valid trading pair</p>
          </div>
        </div>
      )}
      <div
        ref={containerRef}
        className="flex-1 min-h-0 w-full"
      />
    </div>
  );
};

export default TradingChart;
